import { Fragment, useEffect, useMemo, useState } from "react";

import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { PrcDialogContent } from "./PrcDialogContent";
import { Input } from "../../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table";
import { Textarea } from "../../ui/textarea";
import { cn } from "../../utils/common/cn";
import { hasTier } from "./access-control";
import {
  TERM_MONTHS,
  batchScopeLabel,
  industryLabel,
  useFairValueSpreadProposals,
  useFairValueSpreads,
  usePendingFairValueSpreadBatches,
  useProposeFairValueSpreadBatch,
  useReviewFairValueSpreadBatch,
  type FvBatchGradeDetail,
  type FvBatchGradeInput,
} from "../../utils/portfolio-risk-concentration/api/fairValueSpread";

/** All Industries — a sentinel Select value, never a real borrowerIndustry. */
const ALL_INDUSTRIES = "__ALL_INDUSTRIES__";
/** All Terms — a sentinel Select value, never a real termMonths. Kept as a string since the
 *  Term Select's items are otherwise real term numbers stringified. */
const ALL_TERMS = "__ALL_TERMS__";

/** "8.00 → 7.50", "varies → 7.50" when a fan-out batch's leaves didn't all start at the same
 *  spread (mixedOld), or "—" either side when a spread isn't set. */
function spreadDiff(oldVal: number | null, newVal: number | null, mixedOld: boolean): string {
  const fmt = (v: number | null) => (v === null ? "—" : v.toFixed(2));
  const old = mixedOld ? "varies" : fmt(oldVal);
  return `${old} → ${fmt(newVal)}`;
}

/** The per-grade before/after breakdown — one row per grade in the batch, showing exactly
 *  what changed for SOFR and Treasury spread. Shared by the approval queue and the log, since
 *  a reviewer and a historical reader both need the same "what actually changed" table. A
 *  fan-out batch (All Industry / All Terms) can hold many leaves per grade — leafCount shows
 *  how many, since the diff itself only ever names one new value, applied everywhere. */
function BatchGradeBreakdown({ grades }: { grades: FvBatchGradeDetail[] }) {
  return (
    <div className="mt-2 overflow-hidden rounded-md border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="h-8 text-[11px]">Grade</TableHead>
            <TableHead className="h-8 text-right text-[11px]">SOFR Spread</TableHead>
            <TableHead className="h-8 text-right text-[11px]">Treasury Spread</TableHead>
            <TableHead className="h-8 text-right text-[11px]">Leaves</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {grades.map((g) => (
            <TableRow key={g.creditGrade}>
              <TableCell className="py-1.5 text-xs font-medium">{g.creditGrade}</TableCell>
              <TableCell className="py-1.5 text-right text-xs tabular-nums">
                {spreadDiff(g.oldSofrSpreadPct, g.newSofrSpreadPct, g.mixedOld)}
              </TableCell>
              <TableCell className="py-1.5 text-right text-xs tabular-nums">
                {spreadDiff(g.oldTreasurySpreadPct, g.newTreasurySpreadPct, g.mixedOld)}
              </TableCell>
              <TableCell className="py-1.5 text-right text-xs tabular-nums text-muted-foreground">
                {g.leafCount}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

const LOG_PAGE_SIZE = 10;

/**
 * Discount & Spread Settings — the FMV tab (Manage Capital's Segment 3). A facility's
 * Treasury/SOFR spread matrix, one
 * row per Credit Grade, filtered to one Industry and one Term band at a time.
 *
 * ── Only the FMV half of Segment 3 ───────────────────────────────────────────────────────
 * Segment 3 ("Discount & Spread Settings") also covers a DCB discount rate per pool — see
 * `discountSettings.ts` and the per-facility summary table in `ManageCapitalPage.tsx`, which
 * renders this component inside a Dialog when "Adjust" is clicked on the FMV column. This
 * component itself is the FMV method only, i.e. "what's active for Treasury and SOFR, and
 * the ability to adjust the spreads for both". Book Value (Net Investment vs Financed) is a
 * still-unbuilt third valuation method; Zero-Out Rules is real now — see Segment 4 and
 * `zeroOutRules.ts` — and is a sibling setting to this one, not part of it.
 *
 * ── Why "Propose new version" submits every grade at once ───────────────────────────────
 * The matrix is one version: every grade's SOFR and Treasury spread for the selected
 * (Industry, Term band), reviewed and applied together rather than row by row. See
 * fair_value.py's module docstring for how that batch is represented against a proposals
 * table that only holds one grade's values per row.
 */
export function ContractValuationSettings({ facilityName }: { facilityName?: string }) {
  const gridQ = useFairValueSpreads();
  const leaves = useMemo(() => gridQ.data?.leaves ?? [], [gridQ.data]);

  const industries = useMemo(
    () => Array.from(new Set(leaves.map((l) => l.borrowerIndustry))).sort(),
    [leaves],
  );

  const [industry, setIndustry] = useState<string>("");
  const [termSel, setTermSel] = useState<string>("");
  const isAllIndustries = industry === ALL_INDUSTRIES;
  const isAllTerms = termSel === ALL_TERMS;
  const termMonths = isAllTerms ? null : Number(termSel) || null;

  // Default to the first REAL industry/term once the grid loads, never to "All" — a wildcard
  // scope is something the viewer opts into deliberately, not a default that could fan a
  // proposal out to the whole book by accident. Re-running only when the CURRENT selection
  // stops existing in the data avoids clobbering a choice the viewer already made.
  useEffect(() => {
    if (industry && (industry === ALL_INDUSTRIES || industries.includes(industry))) return;
    if (industries.length) setIndustry(industries[0]);
  }, [industries, industry]);

  useEffect(() => {
    if (termSel && (termSel === ALL_TERMS || TERM_MONTHS.includes(Number(termSel)))) return;
    setTermSel(String(TERM_MONTHS[0]));
  }, [termSel]);

  // Every leaf the current filter covers — the source of both the editable grade list below
  // and each grade's prefill.
  const rows = useMemo(
    () =>
      leaves.filter(
        (l) =>
          (isAllIndustries || l.borrowerIndustry === industry) &&
          (isAllTerms || l.termMonths === termMonths),
      ),
    [leaves, industry, termMonths, isAllIndustries, isAllTerms],
  );

  // The grid is SPARSE — grade coverage is NOT uniform across industries (e.g. AWNINGS only
  // has leaves for A1 and B2, not the other 9 grades that exist elsewhere). So the editable
  // grade list is always derived from `rows`, never the whole grid: with no wildcard, that's
  // exactly the grades this one (industry, term) actually has, same as before this feature;
  // wildcarding either filter widens it to the UNION of grades that have at least one real
  // leaf anywhere in the wildcarded scope. The backend resolves the same way when it fans a
  // proposal out, so a grade never gets proposed against a combination with no real leaf.
  const grades = useMemo(
    () => Array.from(new Set(rows.map((r) => r.creditGrade))).sort(),
    [rows],
  );

  // Draft spread values, keyed by grade — reset whenever the filter selection changes, or the
  // grid refetches after an approval. Prefilled from the live grid ONLY when every leaf the
  // current filter covers for that grade agrees on a value; a wildcarded filter (All Industry
  // and/or All Terms) that covers leaves with DIFFERING current spreads leaves the field blank
  // rather than showing one arbitrary leaf's value as if it were universal — the same
  // reasoning the batch log's "varies" applies after the fact.
  const [draft, setDraft] = useState<Record<string, { treasury: string; sofr: string }>>({});
  // Which grades were left blank because the wildcarded leaves DISAGREED, rather than because
  // there was genuinely nothing set — drives the "varies" placeholder below, so a blank field
  // never looks indistinguishable from an ordinary unset one.
  const [draftVaries, setDraftVaries] = useState<Record<string, boolean>>({});
  useEffect(() => {
    const single = (vals: (number | null)[]): number | null => {
      const distinct = new Set(vals);
      return distinct.size === 1 ? [...distinct][0] : null;
    };
    const nextDraft: Record<string, { treasury: string; sofr: string }> = {};
    const nextVaries: Record<string, boolean> = {};
    for (const g of grades) {
      const matching = rows.filter((r) => r.creditGrade === g);
      const treasuryVals = matching.map((r) => r.treasurySpreadPct);
      const sofrVals = matching.map((r) => r.sofrSpreadPct);
      const treasury = matching.length ? single(treasuryVals) : null;
      const sofr = matching.length ? single(sofrVals) : null;
      nextDraft[g] = {
        treasury: treasury == null ? "" : String(treasury),
        sofr: sofr == null ? "" : String(sofr),
      };
      nextVaries[g] =
        matching.length > 1 &&
        (new Set(treasuryVals).size > 1 || new Set(sofrVals).size > 1);
    }
    setDraft(nextDraft);
    setDraftVaries(nextVaries);
  }, [grades, rows]);

  const [proposeOpen, setProposeOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  return (
    <div className="mt-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">
            Discount & Spread Settings{facilityName ? ` · ${facilityName}` : ""}
          </p>
          <h3 className="mt-1 text-sm font-semibold">Spread Matrix</h3>
          <p className="mt-0.5 max-w-2xl text-xs text-muted-foreground">
            Spread added on top of the selected curve, by grade. The whole table proposes and
            approves as one version.
          </p>
        </div>
        <Button
          size="sm"
          disabled={grades.length === 0}
          onClick={() => setProposeOpen(true)}
        >
          Propose New Version
        </Button>
      </div>

      {toast && (
        <div className="mt-3 rounded-md border border-[color:var(--approve)]/40 bg-[color:var(--approve)]/10 px-3 py-2 text-xs">
          {toast}
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-3">
        <div className="w-48">
          <Select value={industry} onValueChange={setIndustry}>
            <SelectTrigger aria-label="Industry">
              <SelectValue placeholder="Industry" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_INDUSTRIES}>All Industries</SelectItem>
              {industries.map((i) => (
                <SelectItem key={i} value={i}>
                  {industryLabel(i)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-40">
          <Select value={termSel} onValueChange={setTermSel}>
            <SelectTrigger aria-label="Term band">
              <SelectValue placeholder="Term band" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_TERMS}>All Terms</SelectItem>
              {TERM_MONTHS.map((t) => (
                <SelectItem key={t} value={String(t)}>
                  {t} months
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {(isAllIndustries || isAllTerms) && (
        <p className="mt-2 text-xs text-muted-foreground">
          {isAllIndustries && isAllTerms
            ? `One value per grade below will be proposed for every one of the ${industries.length} industries × ${TERM_MONTHS.length} terms.`
            : isAllIndustries
              ? `One value per grade below will be proposed for every one of the ${industries.length} industries, at ${termMonths}mo.`
              : `One value per grade below will be proposed for every one of the ${TERM_MONTHS.length} terms, in ${industryLabel(industry)}.`}
        </p>
      )}

      <div className="mt-3 overflow-hidden rounded-lg border border-border">
        <Table className="table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className="w-1/3 px-3 py-2">Grade</TableHead>
              <TableHead className="w-1/3 px-3 py-2 text-right">SOFR Spread</TableHead>
              <TableHead className="w-1/3 px-3 py-2 text-right">Treasury Spread</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {grades.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} className="py-8 text-center text-sm text-muted-foreground">
                  {gridQ.isLoading ? "Loading spread grid…" : "No grades in the spread grid yet."}
                </TableCell>
              </TableRow>
            )}
            {grades.map((g) => (
              <TableRow key={g}>
                <TableCell className="px-3 py-2 font-medium">{g}</TableCell>
                <TableCell className="px-3 py-2 text-right">
                  <Input
                    type="number"
                    step="0.01"
                    className="ml-auto h-8 w-full max-w-24 text-right"
                    placeholder={draftVaries[g] ? "varies" : undefined}
                    value={draft[g]?.sofr ?? ""}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        [g]: { ...d[g], sofr: e.target.value },
                      }))
                    }
                  />
                </TableCell>
                <TableCell className="px-3 py-2 text-right">
                  <Input
                    type="number"
                    step="0.01"
                    className="ml-auto h-8 w-full max-w-24 text-right"
                    placeholder={draftVaries[g] ? "varies" : undefined}
                    value={draft[g]?.treasury ?? ""}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        [g]: { ...d[g], treasury: e.target.value },
                      }))
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <FairValueApprovalQueue onDecided={(m) => setToast(m)} />
      <FairValueLog />

      <ProposeVersionDialog
        open={proposeOpen}
        onOpenChange={setProposeOpen}
        borrowerIndustry={industry}
        isAllIndustries={isAllIndustries}
        industryCount={industries.length}
        termMonths={termMonths}
        isAllTerms={isAllTerms}
        grades={grades}
        draft={draft}
        onSubmitted={(m) => {
          setToast(m);
          setProposeOpen(false);
        }}
      />
    </div>
  );
}

function ProposeVersionDialog({
  open,
  onOpenChange,
  borrowerIndustry,
  isAllIndustries,
  industryCount,
  termMonths,
  isAllTerms,
  grades,
  draft,
  onSubmitted,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The one concrete industry currently selected — ignored (but still passed) when
   *  isAllIndustries is true. */
  borrowerIndustry: string;
  isAllIndustries: boolean;
  /** Real industry count in the grid, for the "N Industries" description when wildcarded. */
  industryCount: number;
  termMonths: number | null;
  isAllTerms: boolean;
  grades: string[];
  draft: Record<string, { treasury: string; sofr: string }>;
  onSubmitted: (message: string) => void;
}) {
  const propose = useProposeFairValueSpreadBatch();
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason("");
      setError(null);
    }
  }, [open]);

  const industryText = isAllIndustries
    ? `${industryCount} Industr${industryCount === 1 ? "y" : "ies"}`
    : borrowerIndustry || "—";
  const termText = isAllTerms ? "All Terms" : termMonths != null ? `${termMonths}mo` : "—";

  async function submit() {
    if (!reason.trim()) {
      setError("A reason is required to raise a proposal.");
      return;
    }
    if ((!isAllIndustries && !borrowerIndustry) || (!isAllTerms && !termMonths) || grades.length === 0) {
      return;
    }

    const parsed = (v: string): number | undefined => {
      const n = Number(v);
      return v.trim() === "" || !Number.isFinite(n) ? undefined : n;
    };
    const payload: FvBatchGradeInput[] = grades.map((g) => ({
      creditGrade: g,
      sofrSpreadPct: parsed(draft[g]?.sofr ?? "") ?? null,
      treasurySpreadPct: parsed(draft[g]?.treasury ?? "") ?? null,
    }));

    try {
      const res = await propose.mutateAsync({
        borrowerIndustry: isAllIndustries ? undefined : borrowerIndustry,
        allIndustries: isAllIndustries,
        termMonths: isAllTerms ? undefined : (termMonths ?? undefined),
        allTerms: isAllTerms,
        reason: reason.trim(),
        grades: payload,
      });
      onSubmitted(
        `New version proposed for ${industryText} · ${termText} — ${res.leafCount} leaf value${
          res.leafCount === 1 ? "" : "s"
        } awaiting review.`,
      );
    } catch (err) {
      // A 409 ("A · AUTOMOTIVE · 24mo already has a proposal awaiting review.") or a 400
      // arrives as the FastAPI `detail` string in the response body, not in err.message —
      // axios's own message is just "Request failed with status code 409". Same extraction
      // EligibilityConditionDialog/InvestorCodeDialog already use for the same reason.
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(
        detail || (err instanceof Error ? err.message : null) || "Could not submit the proposal.",
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <PrcDialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Propose New Version</DialogTitle>
          <DialogDescription>
            Every grade shown for {industryText} · {termText} is proposed together, as one
            version. Nothing changes until it is approved.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {error && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {error}
            </div>
          )}
          <Textarea
            rows={3}
            placeholder="Reason for this change (required)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={propose.isPending} onClick={submit}>
            {propose.isPending ? "Submitting…" : "Submit proposal"}
          </Button>
        </DialogFooter>
      </PrcDialogContent>
    </Dialog>
  );
}

/** The approval queue — every PENDING batch, notes mandatory before Approve/Reject, same
 *  contract as every other PRC review surface. */
function FairValueApprovalQueue({ onDecided }: { onDecided: (message: string) => void }) {
  const queueQ = usePendingFairValueSpreadBatches();
  const review = useReviewFairValueSpreadBatch();
  const canDecide = hasTier("contributor");

  const [notes, setNotes] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  const batches = queueQ.data?.batches ?? [];

  async function act(batchId: string, decision: "APPROVED" | "REJECTED") {
    const note = (notes[batchId] ?? "").trim();
    if (!note) {
      setErrors((e) => ({ ...e, [batchId]: "Review notes are required." }));
      return;
    }
    try {
      setBusy(batchId);
      const res = await review.mutateAsync({ batchId, decision, notes: note });
      if (!res.ok) throw new Error(res.error ?? "Review failed.");
      setNotes((n) => ({ ...n, [batchId]: "" }));
      setErrors((e) => ({ ...e, [batchId]: "" }));
      onDecided(
        decision === "APPROVED"
          ? `${res.gradeCount ?? ""} leaf value(s) approved and applied.`
          : "Batch rejected.",
      );
    } catch (err) {
      setErrors((e) => ({
        ...e,
        [batchId]: err instanceof Error ? err.message : "Review failed.",
      }));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="text-sm font-semibold">
          Approval Queue{batches.length ? ` (${batches.length})` : ""}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {batches.length === 0 ? (
          <p className="text-sm text-muted-foreground">No proposals awaiting review.</p>
        ) : (
          <div className="space-y-4">
            {batches.map((b) => (
              <div key={b.batchId} className="rounded-lg border border-border p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 text-sm">
                    <p className="font-medium">{batchScopeLabel(b)}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      proposed by {b.proposedByRole}
                    </p>
                    {b.reason && <p className="mt-1 text-xs">{b.reason}</p>}
                  </div>
                  <Badge
                    variant="outline"
                    className="border-primary/50 text-[10px] uppercase tracking-[0.1em] text-primary"
                  >
                    Pending
                  </Badge>
                </div>

                <BatchGradeBreakdown grades={b.grades} />

                {canDecide && (
                  <div className="mt-3 space-y-2">
                    <Textarea
                      rows={2}
                      placeholder="Review notes (required)"
                      value={notes[b.batchId] ?? ""}
                      onChange={(e) => setNotes((n) => ({ ...n, [b.batchId]: e.target.value }))}
                    />
                    {errors[b.batchId] && (
                      <p className="text-xs text-destructive">{errors[b.batchId]}</p>
                    )}
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        disabled={busy === b.batchId}
                        onClick={() => act(b.batchId, "APPROVED")}
                      >
                        {busy === b.batchId ? "Applying…" : "Approve"}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={busy === b.batchId}
                        onClick={() => act(b.batchId, "REJECTED")}
                      >
                        Reject
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** The permanent log — every batch, every status, newest first, paginated: one batch can now
 *  produce several rows underneath (one per grade), so this history grows faster than this
 *  page family's other proposal logs, which don't currently page. */
function FairValueLog() {
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const logQ = useFairValueSpreadProposals({ limit: LOG_PAGE_SIZE, offset });
  const batches = logQ.data?.batches ?? [];
  const total = logQ.data?.total ?? 0;

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="text-sm font-semibold">Spread Version History</CardTitle>
      </CardHeader>
      <CardContent className="px-0">
        <div className="overflow-hidden rounded-lg border border-border">
          <Table className="table-fixed">
            <TableHeader>
              <TableRow>
                <TableHead className="w-8 px-2 py-2" />
                <TableHead className="w-[12%] px-2 py-2">Status</TableHead>
                <TableHead className="w-[16%] px-2 py-2">Scope</TableHead>
                <TableHead className="w-[32%] px-2 py-2">Reason</TableHead>
                <TableHead className="w-[15%] px-2 py-2">Proposed By</TableHead>
                <TableHead className="w-[25%] px-2 py-2">Reviewed By</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
            {batches.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">
                  Nothing proposed yet.
                </TableCell>
              </TableRow>
            )}
            {batches.map((b) => (
              <Fragment key={b.batchId}>
                <TableRow>
                  <TableCell className="px-2 py-2 pr-0">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0 text-xs"
                      onClick={() => setExpanded((e) => ({ ...e, [b.batchId]: !e[b.batchId] }))}
                    >
                      {expanded[b.batchId] ? "−" : "+"}
                    </Button>
                  </TableCell>
                  <TableCell className="px-2 py-2">
                    <Badge
                      variant="outline"
                      className={cn(
                        "text-[10px] uppercase tracking-[0.1em]",
                        b.status === "APPROVED"
                          ? "border-[color:var(--approve)]/50 text-[color:var(--approve)]"
                          : b.status === "REJECTED"
                            ? "border-destructive/50 text-destructive"
                            : "border-primary/50 text-primary",
                      )}
                    >
                      {b.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="break-words px-2 py-2 text-xs">
                    {batchScopeLabel(b)}
                  </TableCell>
                  <TableCell className="break-words px-2 py-2 text-xs">{b.reason}</TableCell>
                  <TableCell className="break-words px-2 py-2 text-xs text-muted-foreground">
                    {b.proposedByRole}
                  </TableCell>
                  <TableCell className="break-words px-2 py-2 text-xs text-muted-foreground">
                    {b.reviewedByRole ? (
                      <>
                        {b.reviewedByRole}
                        {b.reviewNotes && <div className="text-[11px]">{b.reviewNotes}</div>}
                      </>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                </TableRow>
                {expanded[b.batchId] && (
                  <TableRow>
                    <TableCell />
                    <TableCell colSpan={5} className="pb-3 pt-0">
                      <BatchGradeBreakdown grades={b.grades} />
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            ))}
          </TableBody>
        </Table>
        </div>
        <div className="flex items-center justify-between px-4 pt-3 text-xs text-muted-foreground">
          <span>
            {total === 0 ? "0 of 0" : `${offset + 1}–${Math.min(offset + LOG_PAGE_SIZE, total)} of ${total}`}
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs"
              disabled={offset === 0}
              onClick={() => setOffset((o) => Math.max(0, o - LOG_PAGE_SIZE))}
            >
              Prev
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs"
              disabled={offset + LOG_PAGE_SIZE >= total}
              onClick={() => setOffset((o) => o + LOG_PAGE_SIZE)}
            >
              Next
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
